/**
 * Relevance ranking of the global search: merges the hits of all areas into one list, best match first.
 *
 * The finder returns the hits per area (one elasticsearch query per index); raw `_score` values are not comparable
 * across indices (different term statistics and field boosts). The rank therefore combines
 * - the score normalized within its area (best hit of an area = 1) and, weaker, relative to the best score overall,
 * - strong boosts for unambiguous matches computed from the visible hit data: exact number (customId), exact
 *   e-mail / phone, exact full name, name words matched exactly or as prefix,
 * - a penalty for inactive profiles (a strong inactive match still beats a weak active one).
 *
 * Pure functions without Vue / store imports (checked by tests/search/searchRanking.check.ts).
 */

/** Minimal hit shape used by the ranking (finder hits and trainings) */
export type RankableHit = {
    id: string;
    entity: string;
    title: string;
    customId?: string;
    isActive?: boolean;
    matchedOn?: string[];
    score: number;
    preview?: Record<string, unknown>;
};

export type RankGroups<T extends RankableHit> = Partial<Record<string, { items: T[] }>>;

export const RANK_WEIGHTS = {
    /** Term equals the number (Kundennummer, Personalnummer, Kursnummer, …) */
    exactCustomId: 100,
    /** Term equals a visible e-mail address / phone number of the hit */
    exactContact: 60,
    /** The finder reports an e-mail / phone match and the term looks like one (contact not visible in the hit) */
    contactMatch: 30,
    /** All words of the term are the words of the name (any order) */
    exactName: 40,
    /** Share of term words found in the name: exact word 1, word prefix 0.75, inside a word 0.3 */
    nameCoverage: 25,
    /** The name starts with the whole term */
    namePrefix: 5,
    /** The number starts with the term (at least 3 characters) */
    customIdPrefix: 20,
    /** Elasticsearch relevance (0.6 normalized within the area + 0.4 relative to the best score overall) */
    score: 12,
    /** Inactive / deactivated profiles */
    inactive: -10,
};

const MIN_CUSTOM_ID_PREFIX = 3;

/** Lower case, umlauts / ß folded (ä → ae), other diacritics removed */
export function normalizeText(value: string | undefined | null): string {
    return (value || '')
        .toLowerCase()
        .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .trim();
}

/** Words of a name or term (whitespace, hyphen, comma, slash and dots separate words) */
export function wordsOf(value: string | undefined | null): string[] {
    return normalizeText(value).split(/[\s\-,;/\\.·()]+/).filter((el) => el.length > 0);
}

/** Number / id comparison: case and whitespace insensitive */
function compactId(value: string | undefined | null): string {
    return (value || '').toLowerCase().replace(/\s+/g, '');
}

function isMailLike(term: string): boolean {
    return term.includes('@') && !/\s/.test(term.trim());
}

/** Digits of a phone number in national form ("+49 151 / 123" → "0151123"), '' if the term is no phone number */
export function phoneKey(value: unknown): string {
    if (typeof value !== 'string' || !/^[\d\s+()/.-]+$/.test(value)) {
        return '';
    }
    let compact = value.replace(/\(0\)/g, '').replace(/[^\d+]/g, '');
    if (compact.startsWith('+49')) {
        compact = '0' + compact.slice(3).replace(/^0+/, '');
    } else if (compact.startsWith('0049')) {
        compact = '0' + compact.slice(4).replace(/^0+/, '');
    }
    compact = compact.replace(/\D/g, '');
    return compact.length >= 4 ? compact : '';
}

/** Visible contact values of a hit (own contact data; parents of a student only if the user may read them) */
function contactValues(hit: RankableHit): { mails: string[]; phones: string[] } {
    const preview = hit.preview || {};
    const mails: string[] = [];
    const phones: string[] = [];
    const collect = (source: Record<string, unknown>) => {
        for (const [key, value] of Object.entries(source)) {
            if (typeof value !== 'string' || !value) {
                continue;
            }
            if (/mail/i.test(key)) {
                mails.push(value.trim().toLowerCase());
            } else if (/phone|mobile/i.test(key)) {
                phones.push(phoneKey(value));
            }
        }
    };
    collect(preview);
    if (Array.isArray(preview.parents)) {
        for (const parent of preview.parents) {
            if (parent && typeof parent === 'object') {
                collect(parent as Record<string, unknown>);
            }
        }
    }
    return {mails, phones: phones.filter(Boolean)};
}

/** Score of a term word against the words of the name: exact 1, prefix 0.75, inside a word 0.3, otherwise 0 */
function wordMatch(word: string, nameWords: string[]): number {
    let best = 0;
    for (const nameWord of nameWords) {
        if (nameWord === word) {
            return 1;
        }
        if (nameWord.startsWith(word)) {
            best = Math.max(best, 0.75);
        } else if (word.length >= 3 && nameWord.includes(word)) {
            best = Math.max(best, 0.3);
        }
    }
    return best;
}

/**
 * Rank of a single hit. `groupMax` = best elasticsearch score of the hit's area, `globalMax` = best score overall.
 * Higher is better; the absolute value has no meaning outside one result list.
 */
export function rankHit(hit: RankableHit, term: string, groupMax: number, globalMax: number): number {
    const trimmed = (term || '').trim();
    if (!trimmed) {
        return 0;
    }
    let rank = 0;

    const score = typeof hit.score === 'number' && hit.score > 0 ? hit.score : 0;
    // Areas without elasticsearch score (trainings) are neutral
    const groupNorm = groupMax > 0 ? score / groupMax : 0.5;
    const globalNorm = globalMax > 0 ? score / globalMax : 0;
    rank += RANK_WEIGHTS.score * (0.6 * groupNorm + 0.4 * globalNorm);

    // Number
    const termId = compactId(trimmed);
    const customId = compactId(hit.customId);
    if (customId && termId) {
        if (customId === termId) {
            rank += RANK_WEIGHTS.exactCustomId;
        } else if (termId.length >= MIN_CUSTOM_ID_PREFIX && customId.startsWith(termId)) {
            rank += RANK_WEIGHTS.customIdPrefix;
        }
    }

    // Contact data
    const matchedOn = hit.matchedOn || [];
    const termPhone = phoneKey(trimmed);
    if (isMailLike(trimmed) || termPhone) {
        const {mails, phones} = contactValues(hit);
        const exact = isMailLike(trimmed) ? mails.includes(trimmed.toLowerCase()) : phones.includes(termPhone);
        const reported = matchedOn.some((label) => (isMailLike(trimmed) ? /mail/i : /telefon|phone|mobile/i).test(label));
        if (exact) {
            rank += RANK_WEIGHTS.exactContact;
        } else if (reported) {
            rank += RANK_WEIGHTS.contactMatch;
        }
    }

    // Name (title): courses have their number as title, it is already covered above
    if (hit.entity !== 'courses') {
        const termWords = wordsOf(trimmed);
        const nameWords = wordsOf(hit.title);
        if (termWords.length && nameWords.length) {
            const sortedTerm = [...termWords].sort().join(' ');
            const sortedName = [...nameWords].sort().join(' ');
            if (sortedTerm === sortedName) {
                rank += RANK_WEIGHTS.exactName;
            }
            const coverage = termWords.reduce((sum, word) => sum + wordMatch(word, nameWords), 0) / termWords.length;
            rank += RANK_WEIGHTS.nameCoverage * coverage;
            if (normalizeText(hit.title).startsWith(normalizeText(trimmed))) {
                rank += RANK_WEIGHTS.namePrefix;
            }
        }
    }

    if (hit.isActive === false) {
        rank += RANK_WEIGHTS.inactive;
    }
    return rank;
}

/**
 * Merges the hits of all areas into one list ordered by rank. Ties keep the elasticsearch order within an area,
 * then the order of `entityOrder` (areas not listed come last).
 */
export function rankHits<T extends RankableHit>(groups: RankGroups<T>, term: string, entityOrder: string[] = []): T[] {
    const entries: Array<{ hit: T; rank: number; position: number; areaIndex: number }> = [];
    const areas = Object.keys(groups)
        .map((key) => ({key, items: groups[key]?.items || []}))
        .filter((area) => area.items.length > 0);
    const maxOf = (items: T[]) => items.reduce((max, hit) => Math.max(max, typeof hit.score === 'number' ? hit.score : 0), 0);
    const globalMax = areas.reduce((max, area) => Math.max(max, maxOf(area.items)), 0);
    for (const area of areas) {
        const groupMax = maxOf(area.items);
        const order = entityOrder.indexOf(area.key);
        area.items.forEach((hit, position) => {
            entries.push({hit, rank: rankHit(hit, term, groupMax, globalMax), position, areaIndex: order < 0 ? entityOrder.length : order});
        });
    }
    entries.sort((a, b) => b.rank - a.rank || a.position - b.position || a.areaIndex - b.areaIndex);
    return entries.map((entry) => entry.hit);
}
